import { useState, useEffect, useCallback } from 'react';
import { supabase } from './lib/supabase';
import OrgSetupWizard from './OrgSetupWizard';
import AvailabilityScreen from './AvailabilityScreen';

const WEEK_OF = '2026-03-23';

const COLORS = {
  navy: '#0d1f4e',
  navyLight: '#152a63',
  green: '#0f7a62',
  greenBright: '#22c57a',
  bg: '#f5f7fb',
  white: '#ffffff',
  border: '#dbe0ed',
  textMuted: '#8a93b0',
  textBody: '#4a5578',
};

const pill = (bg, color, text) => (
  <span style={{ background: bg, color, padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700 }}>{text}</span>
);

// Turns a 24hr "HH:MM" string into 12hr "H:MM AM/PM" for display.
// Defensive: returns '' for anything missing or malformed so the cell never crashes.
const formatTime = (t) => {
  if (!t || typeof t !== 'string' || !t.includes(':')) return '';
  const [hStr, mStr] = t.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (isNaN(h) || isNaN(m)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

// Builds the full "9:00 AM - 5:00 PM" display from start/end columns.
// Falls back to whichever clean value exists if only one is present..
const formatShiftTime = (shift) => {
  const start = formatTime(shift.start_time);
  const end = formatTime(shift.end_time);
  if (start && end) return `${start} - ${end}`;
  return start || end || '';
};

// "2026-03-23" -> "March 23, 2026". Parsed by hand so there's no timezone shift
// (a `date` column read as a Date would drift a day in western timezones).
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const formatDate = (d) => {
  if (!d || typeof d !== 'string') return '';
  const [y, m, day] = d.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !day) return d;
  return `${MONTHS[m - 1]} ${day}, ${y}`;
};

// ─────────────────────────────────────────────
// LOGIN GATE
// Shows a magic-link login screen until a user is
// authenticated, then renders the dashboard.
// ─────────────────────────────────────────────
function LoginScreen() {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function sendMagicLink() {
    if (!email.trim()) return;
    setSending(true);
    setError('');
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) {
      setError(error.message || 'Something went wrong. Try again.');
      setSending(false);
    } else {
      setSent(true);
      setSending(false);
    }
  }

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: COLORS.navy, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '40px 36px', width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ fontWeight: 800, fontSize: 26, letterSpacing: '-0.03em', color: COLORS.navy, marginBottom: 6 }}>
          THRIVE<span style={{ color: COLORS.greenBright }}>AI</span>
        </div>

        {sent ? (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: COLORS.navy, margin: '20px 0 8px', letterSpacing: '-0.02em' }}>Check your email</h1>
            <p style={{ color: COLORS.textBody, fontSize: 14, lineHeight: 1.5, margin: 0 }}>
              We sent a login link to <strong style={{ color: COLORS.navy }}>{email}</strong>. Click the link in that email to sign in. You can close this tab.
            </p>
            <button
              onClick={() => { setSent(false); setEmail(''); }}
              style={{ marginTop: 24, background: 'none', border: 'none', color: COLORS.green, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}
            >
              ← Use a different email
            </button>
          </>
        ) : (
          <>
            <p style={{ color: COLORS.textBody, fontSize: 14, lineHeight: 1.5, margin: '8px 0 24px' }}>
              Enter your email and we'll send you a secure login link. No password needed.
            </p>

            <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') sendMagicLink(); }}
              placeholder="you@example.com"
              style={{ width: '100%', padding: '12px 14px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, color: COLORS.navy, fontFamily: 'Inter, sans-serif', outline: 'none', boxSizing: 'border-box' }}
            />

            {error && (
              <div style={{ background: '#fee2e2', border: '1px solid #b91c1c', borderRadius: 8, padding: '9px 12px', marginTop: 12, color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>
                {error}
              </div>
            )}

            <button
              onClick={sendMagicLink}
              disabled={sending || !email.trim()}
              style={{
                width: '100%', marginTop: 20, padding: '13px', borderRadius: 8, border: 'none',
                background: (sending || !email.trim()) ? COLORS.textMuted : `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.green})`,
                color: '#fff', fontSize: 14, fontWeight: 700,
                cursor: (sending || !email.trim()) ? 'not-allowed' : 'pointer',
              }}
            >
              {sending ? 'Sending...' : 'Send login link'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function App() {
  // ── AUTH STATE ──
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // ── ORG STATE ──
  // The org owned by the logged-in user. Resolved from the session, not
  // hardcoded — this is what makes the app multi-tenant. Null until resolved.
  const [orgId, setOrgId] = useState(null);
  // Whether the org lookup for the current session has resolved yet. Gates the
  // main shell so we can decide the landing tab from setup_completed_at instead
  // of flashing the wrong tab. Reset to false when the session clears.
  const [orgLoaded, setOrgLoaded] = useState(false);

  useEffect(() => {
    // Check for an existing session on load
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });

    // Listen for login / logout
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Once we have a session, look up the org this user owns.
  useEffect(() => {
    if (!session) { setOrgId(null); setOrgLoaded(false); setTab(null); return; }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, setup_completed_at')
        .eq('owner_id', session.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) console.error('Org lookup error:', error);
      setOrgId(data ? data.id : null);
      // Decide the landing tab only while it's still undecided (null), so a later
      // token-refresh re-run of this effect never yanks the manager off her tab.
      setTab(prev => prev ?? (data && data.setup_completed_at ? 'schedule' : 'setup'));
      setOrgLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [session]);

  const [tab, setTab] = useState(null); // decided after the org loads (see org lookup effect)
  const [schedule, setSchedule] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState('');

  // ── SCHEDULE RUN STATE ──
  // The active availability-gathering run (status 'draft' or 'sent'). Null until
  // one is created or loaded. Send (Step 3) flips draft -> sent.
  const [activeRun, setActiveRun] = useState(null);
  const [runForm, setRunForm] = useState({ label: '', cycle_start: '', cycle_end: '' });
  const [creatingRun, setCreatingRun] = useState(false);
  const [runMsg, setRunMsg] = useState('');

  // Preview breakdown (willReceive / alreadySent / noEmail) from the send
  // endpoint. Drives both the honest panel label and the confirmation modal.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // ── RESPONSE DASHBOARD (Phase 4 Step 4, read-only) ──
  // Derived, per-employee status for the active run: who submitted, who's still
  // awaiting, who never got a link. null until the active run + employees load.
  const [responses, setResponses] = useState(null);

  // Staff editing state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [roles, setRoles] = useState([]);
  const [rolesDrawerOpen, setRolesDrawerOpen] = useState(false);

const loadData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    const { data: shiftsData } = await supabase.from('shifts').select('*').eq('org_id', orgId);
    const { data: assignData } = await supabase.from('schedule_assignments').select('*').eq('org_id', orgId).eq('week_of', WEEK_OF);
    const { data: empData } = await supabase.from('employees').select('*').eq('org_id', orgId).order('name');
    const { data: rolesData } = await supabase.from('roles').select('*').eq('org_id', orgId).order('name');
    // Active run = the most recent run still gathering availability (not yet closed).
    const { data: runData } = await supabase
      .from('schedule_runs')
      .select('*')
      .eq('org_id', orgId)
      .in('status', ['draft', 'sent'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setRoles(rolesData || []);
    setShifts(shiftsData || []);
    setSchedule(assignData || []);
    setEmployees(empData || []);
    setActiveRun(runData || null);
    setLoading(false);
  }, [orgId]);

  // Re-fetch org data whenever the user lands on a data-backed tab (Schedule/Staff).
  // App.js otherwise only loaded once on mount and went stale — this is what keeps the
  // Schedule tab fresh after the wizard adds a shift. Also covers the wizard's onComplete,
  // which flips to the Schedule tab and triggers this reload for free.
  useEffect(() => {
    if (session && orgId && (tab === 'schedule' || tab === 'staff')) loadData();
  }, [tab, session, orgId, loadData]);

  // Call the availability-send serverless function with the manager's JWT.
  // The Resend key never touches the frontend — this only carries the user's
  // Supabase access token, which the function verifies server-side.
  const callSendEndpoint = useCallback(async (body) => {
    const jwt = session ? session.access_token : null;
    const res = await fetch('/api/send-availability-requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data };
  }, [session]);

  // Whenever the active run changes, refresh the send breakdown so the panel
  // label reflects real sent_at state, not just run status. (On localhost this
  // 404s — the /api route only exists on the deployed build — and is ignored.)
  useEffect(() => {
    if (!activeRun) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      try {
        const { ok, data } = await callSendEndpoint({ runId: activeRun.id, preview: true });
        if (!cancelled) setPreview(ok && data && data.preview ? data : null);
      } catch {
        if (!cancelled) setPreview(null);
      }
      if (!cancelled) setPreviewLoading(false);
    })();
    return () => { cancelled = true; };
  }, [activeRun, callSendEndpoint]);

  // Build the read-only response dashboard for the active run. Both tables carry
  // owner-scoped RLS, so the manager reads them directly — no endpoint, no service
  // key. We start from `employees` and join tokens/submissions outward IN JS, so an
  // employee with no email is never dropped; they surface as "No link" (the exact
  // gap this screen exists to catch).
  useEffect(() => {
    if (!activeRun || !orgId || employees.length === 0) { setResponses(null); return; }
    let cancelled = false;
    (async () => {
      const [{ data: tokens }, { data: subs }] = await Promise.all([
        supabase.from('availability_tokens')
          .select('employee_id, sent_at').eq('org_id', orgId).eq('run_id', activeRun.id),
        supabase.from('availability_submissions')
          .select('employee_id, created_at').eq('org_id', orgId).eq('run_id', activeRun.id),
      ]);
      if (cancelled) return;
      // First token per employee (only sent_at matters for status).
      const tokenByEmp = new Map();
      (tokens || []).forEach(t => { if (!tokenByEmp.has(t.employee_id)) tokenByEmp.set(t.employee_id, t); });
      // Submissions are one row PER DAY per run — collapse to one timestamp per
      // person, keeping the latest to represent when they last responded.
      const subByEmp = new Map();
      (subs || []).forEach(s => {
        const prev = subByEmp.get(s.employee_id);
        if (!prev || (s.created_at && s.created_at > prev)) subByEmp.set(s.employee_id, s.created_at);
      });
      const rows = employees.map(e => {
        if (subByEmp.has(e.id)) return { id: e.id, name: e.name, state: 'submitted', when: subByEmp.get(e.id) };
        const tok = tokenByEmp.get(e.id);
        if (e.email && tok && tok.sent_at) return { id: e.id, name: e.name, state: 'awaiting', when: null };
        return { id: e.id, name: e.name, state: 'nolink', when: null };
      });
      const responded = rows.filter(r => r.state === 'submitted').length;
      setResponses({ rows, responded, total: rows.length });
    })();
    return () => { cancelled = true; };
  }, [activeRun, orgId, employees]);

  function getAssignment(shiftId) {
    return schedule.find(a => a.shift_id === shiftId);
  }

  const days = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
  const shiftsByDay = days.map(day => ({
    day,
    shifts: shifts.filter(s => s.day === day)
  })).filter(d => d.shifts.length > 0);

  const totalHours = schedule.reduce((sum, a) => {
    const shift = shifts.find(s => s.id === a.shift_id);
    const h = shift ? parseFloat(shift.hours) : 0;
    return sum + (a.person_1 ? h : 0) + (a.person_2 ? h : 0);
  }, 0);

  function startEdit(emp) {
    setEditingId(emp.id);
    setEditForm({ ...emp });
    setSaveMsg('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({});
    setSaveMsg('');
  }

  async function saveEdit() {
    setSaving(true);
    setSaveMsg('');
    const { error } = await supabase
      .from('employees')
      .update({
        name: editForm.name,
        role: editForm.role,
        priority: editForm.priority,
        max_hours: editForm.max_hours,
        email: editForm.email,
        phone: editForm.phone,
        certifications: editForm.certifications,
        available_days: editForm.available_days,
        preferred_shifts: editForm.preferred_shifts,
        notes: editForm.notes,
        max_hours_monthly: editForm.max_hours_monthly,
        max_hours_quarterly: editForm.max_hours_quarterly,
      })
      .eq('id', editForm.id);

    if (error) {
      setSaveMsg('❌ Error saving. Try again.');
    } else {
      setSaveMsg('✅ Saved');
      setEmployees(prev => prev.map(e => e.id === editForm.id ? { ...e, ...editForm } : e));
      setTimeout(() => {
        setEditingId(null);
        setEditForm({});
        setSaveMsg('');
      }, 1000);
    }
    setSaving(false);
  }

  // Create a new draft schedule run for this org from the manager's picked dates.
  // type="date" inputs yield 'YYYY-MM-DD' strings that map straight onto the
  // schedule_runs.cycle_start / cycle_end date columns — no timezone shift.
  async function createRun() {
    if (!runForm.cycle_start || !runForm.cycle_end) {
      setRunMsg('Please pick both a start and end date.');
      return;
    }
    if (runForm.cycle_end < runForm.cycle_start) {
      setRunMsg('The end date must be on or after the start date.');
      return;
    }
    setCreatingRun(true);
    setRunMsg('');
    const { data, error } = await supabase
      .from('schedule_runs')
      .insert({
        org_id: orgId,
        label: runForm.label.trim() || null,
        cycle_start: runForm.cycle_start,
        cycle_end: runForm.cycle_end,
        status: 'draft',
      })
      .select()
      .single();
    if (error) {
      setRunMsg('Could not start the run. Please try again.');
      setCreatingRun(false);
      return;
    }
    setActiveRun(data);
    setRunForm({ label: '', cycle_start: '', cycle_end: '' });
    setCreatingRun(false);
  }

  // Open the confirmation modal, refreshing the breakdown first so the manager
  // reviews who will actually receive right now (roster may have changed).
  async function openSendModal() {
    if (!activeRun) return;
    setSendResult(null);
    setShowSendModal(true);
    setPreviewLoading(true);
    try {
      const { ok, data } = await callSendEndpoint({ runId: activeRun.id, preview: true });
      if (ok && data && data.preview) setPreview(data);
    } catch {
      // Leave whatever preview we had; the modal shows a loading/empty state.
    }
    setPreviewLoading(false);
  }

  // Confirmed send. Flips the run to 'sent' and fans out emails server-side.
  async function confirmSend() {
    if (!activeRun) return;
    setSending(true);
    let result;
    try {
      const { ok, data } = await callSendEndpoint({ runId: activeRun.id });
      result = ok ? data : { error: (data && data.error) || 'Something went wrong sending. Please try again.' };
    } catch {
      result = { error: 'Could not reach the server. Please try again.' };
    }
    setSending(false);
    setShowSendModal(false);
    setSendResult(result);
    await loadData(); // refreshes run status + re-triggers the preview effect
  }

  const inputStyle = {
    width: '100%',
    padding: '7px 10px',
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    fontSize: 13,
    color: COLORS.navy,
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    background: '#fff',
  };

  const runLabelStyle = {
    fontSize: 11, fontWeight: 700, color: COLORS.textMuted,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    display: 'block', marginBottom: 5,
  };

  const navTabs = ['setup', 'staff', 'schedule'];

  // ── EMPLOYEE AVAILABILITY LINK ──
  // A ?token=... in the URL means an employee is following their availability
  // link. Render that flow and skip the manager auth gate entirely.
  const availabilityToken = new URLSearchParams(window.location.search).get('token');
  if (availabilityToken) {
    return <AvailabilityScreen token={availabilityToken} />;
  }

  // ── AUTH GATE ──
  // While checking for a session — or while the org lookup that decides the
  // landing tab is still in flight — show a neutral loading state.
  if (authLoading || (session && !orgLoaded)) {
    return (
      <div style={{ fontFamily: 'Inter, sans-serif', background: COLORS.navy, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
        Loading...
      </div>
    );
  }
  // No session → show the login screen, nothing else.
  if (!session) {
    return <LoginScreen />;
  }

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: COLORS.bg, minHeight: '100vh' }}>

      {/* NAV */}
      <div style={{ background: COLORS.navy, padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ color: '#fff', fontWeight: 800, fontSize: 20, letterSpacing: '-0.03em', padding: '16px 0' }}>
          THRIVE<span style={{ color: COLORS.greenBright }}>AI</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {navTabs.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                background: tab === t ? 'rgba(255,255,255,0.12)' : 'transparent',
                color: tab === t ? '#fff' : 'rgba(255,255,255,0.5)',
                border: 'none',
                padding: '20px 20px',
                fontSize: 13,
                fontWeight: tab === t ? 700 : 500,
                cursor: 'pointer',
                textTransform: 'capitalize',
                borderBottom: tab === t ? `2px solid ${COLORS.greenBright}` : '2px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              {t === 'setup' ? '🏗️ Org Setup' : t === 'staff' ? '👥 Staff' : '📅 Schedule'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>{session.user.email}</span>
          <button
            onClick={() => supabase.auth.signOut()}
            style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            Sign out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>

        {/* ── SCHEDULE TAB ── */}
        {tab === 'schedule' && (
          <>
            {/* ── SCHEDULE RUN ── */}
            {/* The active availability-gathering run for this org. Creating one inserts a
                draft schedule_runs row; Send (Step 3) flips it to 'sent'. Active run =
                the most recent run not yet closed. */}
            <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
              {activeRun ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <h2 style={{ fontSize: 18, fontWeight: 800, color: COLORS.navy, margin: 0, letterSpacing: '-0.02em' }}>
                        {activeRun.label || 'Schedule Run'}
                      </h2>
                      {/* Honest status: a 'sent' run only claims "Links live" once at
                          least one token actually carries a sent_at. */}
                      {activeRun.status !== 'sent'
                        ? pill('#fef3c7', '#92661a', 'Draft')
                        : (previewLoading || !preview)
                          ? pill('#eef1f8', '#4a5578', 'Checking links…')
                          : (preview.alreadySent && preview.alreadySent.length > 0)
                            ? pill('#d0f0eb', '#0f7a62', 'Links live')
                            : pill('#fef3c7', '#92661a', 'Links not yet sent')}
                    </div>
                    <p style={{ color: COLORS.textBody, fontSize: 14, margin: '6px 0 0' }}>
                      {formatDate(activeRun.cycle_start)} – {formatDate(activeRun.cycle_end)}
                    </p>
                  </div>
                  <button
                    onClick={openSendModal}
                    style={{
                      background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.green})`,
                      color: '#fff', border: 'none', borderRadius: 8,
                      padding: '11px 22px', fontSize: 14, fontWeight: 700,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    ✉ Send Availability Requests
                  </button>
                </div>
              ) : (
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 800, color: COLORS.navy, margin: '0 0 4px', letterSpacing: '-0.02em' }}>Start a Schedule Run</h2>
                  <p style={{ color: COLORS.textBody, fontSize: 14, margin: '0 0 16px', lineHeight: 1.5 }}>
                    Pick the dates this schedule covers. Employees submit their availability for this window.
                  </p>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ width: 170 }}>
                      <label style={runLabelStyle}>Cycle start</label>
                      <input type="date" style={inputStyle} value={runForm.cycle_start} onChange={e => setRunForm(f => ({ ...f, cycle_start: e.target.value }))} />
                    </div>
                    <div style={{ width: 170 }}>
                      <label style={runLabelStyle}>Cycle end</label>
                      <input type="date" style={inputStyle} value={runForm.cycle_end} onChange={e => setRunForm(f => ({ ...f, cycle_end: e.target.value }))} />
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <label style={runLabelStyle}>Label (optional)</label>
                      <input type="text" style={inputStyle} placeholder="e.g. Week of March 23" value={runForm.label} onChange={e => setRunForm(f => ({ ...f, label: e.target.value }))} />
                    </div>
                    <button
                      onClick={createRun}
                      disabled={creatingRun}
                      style={{
                        background: creatingRun ? COLORS.textMuted : `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.green})`,
                        color: '#fff', border: 'none', borderRadius: 8,
                        padding: '10px 24px', fontSize: 14, fontWeight: 700,
                        cursor: creatingRun ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
                      }}
                    >
                      {creatingRun ? 'Starting...' : 'Start Run'}
                    </button>
                  </div>
                </div>
              )}
              {runMsg && (
                <div style={{ marginTop: 12, color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{runMsg}</div>
              )}
            </div>

            {/* ── RESPONSE DASHBOARD ── read-only: who's responded to the active run.
                Derived from tokens + submissions; starts from the employee list so a
                no-email employee is always shown as "No link". No nudge/resend here. */}
            {activeRun && responses && (
              <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: COLORS.navy, margin: '0 0 4px', letterSpacing: '-0.02em' }}>
                  {responses.responded} of {responses.total} responded
                </h2>
                <p style={{ color: COLORS.textBody, fontSize: 14, margin: '0 0 16px' }}>
                  Availability for {formatDate(activeRun.cycle_start)} – {formatDate(activeRun.cycle_end)}.
                </p>
                <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden' }}>
                  {responses.rows.map((r, i) => (
                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderTop: i === 0 ? 'none' : `1px solid ${COLORS.border}` }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.navy }}>{r.name}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {r.state === 'submitted' && (
                          <span style={{ fontSize: 12, color: COLORS.textMuted }}>{formatDate(r.when)}</span>
                        )}
                        {r.state === 'submitted'
                          ? pill('#d0f0eb', '#0f7a62', 'Submitted')
                          : r.state === 'awaiting'
                            ? pill('#fef3c7', '#92661a', 'Awaiting response')
                            : pill('#fee2e2', '#b91c1c', 'No link')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── SEND RESULT ── honest outcome of the last fan-out */}
            {sendResult && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                background: sendResult.error ? '#fee2e2' : '#d0f0eb',
                border: `1px solid ${sendResult.error ? '#b91c1c' : COLORS.green}`,
                borderRadius: 8, padding: '10px 16px', marginBottom: 24,
                color: sendResult.error ? '#b91c1c' : COLORS.green, fontWeight: 600, fontSize: 14,
              }}>
                <span>
                  {sendResult.error
                    ? `❌ ${sendResult.error}`
                    : `✅ Sent ${sendResult.sent} ${sendResult.sent === 1 ? 'email' : 'emails'}.`
                      + (sendResult.skipped > 0 ? ` ${sendResult.skipped} already had links.` : '')
                      + (sendResult.failed > 0
                        ? ` ${sendResult.failed} failed`
                          + (sendResult.failures && sendResult.failures.length ? ` (${sendResult.failures.map(f => f.name || f.email).join(', ')})` : '')
                          + (sendResult.sendError
                            ? ` — ${sendResult.sendError}`
                            : (sendResult.failures && sendResult.failures[0] && sendResult.failures[0].reason ? ` — ${sendResult.failures[0].reason}` : ''))
                          + '.'
                        : '')}
                </span>
                <button
                  onClick={() => setSendResult(null)}
                  style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* ── SEND CONFIRMATION MODAL ── review before anything sends */}
            {showSendModal && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(13,31,78,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 }}>
                <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                  <h2 style={{ fontSize: 20, fontWeight: 800, color: COLORS.navy, margin: '0 0 12px', letterSpacing: '-0.02em' }}>Send availability requests</h2>

                  {(previewLoading || !preview) ? (
                    <p style={{ color: COLORS.textBody, fontSize: 14, margin: '12px 0' }}>Loading recipients…</p>
                  ) : (
                    <>
                      <p style={{ color: COLORS.textBody, fontSize: 14, lineHeight: 1.5, margin: '0 0 16px' }}>
                        {preview.alreadySent.length > 0
                          ? `${preview.alreadySent.length} ${preview.alreadySent.length === 1 ? 'person' : 'people'} already ${preview.alreadySent.length === 1 ? 'has' : 'have'} their link. Sending to the ${preview.willReceive.length} who ${preview.willReceive.length === 1 ? "doesn't" : "don't"}.`
                          : `Sending availability links to ${preview.willReceive.length} ${preview.willReceive.length === 1 ? 'person' : 'people'}.`}
                      </p>

                      {preview.willReceive.length > 0 && (
                        <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
                          {preview.willReceive.map((p, i) => (
                            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '9px 14px', borderTop: i === 0 ? 'none' : `1px solid ${COLORS.border}` }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.navy }}>{p.name}</span>
                              <span style={{ fontSize: 13, color: COLORS.textMuted }}>{p.email}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {preview.noEmail.length > 0 && (
                        <div style={{ background: '#fff7ed', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92661a', lineHeight: 1.5 }}>
                          ⚠ {preview.noEmail.length} {preview.noEmail.length === 1 ? 'employee has' : 'employees have'} no email and will be skipped: {preview.noEmail.map(p => p.name).join(', ')}.
                        </div>
                      )}
                    </>
                  )}

                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                    <button
                      onClick={() => setShowSendModal(false)}
                      disabled={sending}
                      style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: '#fff', fontSize: 13, fontWeight: 600, color: COLORS.textBody, cursor: sending ? 'not-allowed' : 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmSend}
                      disabled={sending || previewLoading || !preview || preview.willReceive.length === 0}
                      style={{
                        padding: '10px 22px', borderRadius: 8, border: 'none',
                        background: (sending || previewLoading || !preview || preview.willReceive.length === 0) ? COLORS.textMuted : COLORS.greenBright,
                        fontSize: 13, fontWeight: 700, color: COLORS.navy,
                        cursor: (sending || previewLoading || !preview || preview.willReceive.length === 0) ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {sending ? 'Sending…' : `Send ${preview ? preview.willReceive.length : 0} Email${preview && preview.willReceive.length === 1 ? '' : 's'}`}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div>
                <h1 style={{ fontSize: 26, fontWeight: 800, color: COLORS.navy, margin: 0, letterSpacing: '-0.03em' }}>
                  Week of March 23, 2026
                </h1>
                <p style={{ color: COLORS.textBody, marginTop: 4, fontSize: 14 }}>
                  {employees.length} staff · {shifts.length} shifts
                </p>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '10px 20px', fontSize: 14, color: COLORS.navy, fontWeight: 600 }}>
                  {totalHours.toFixed(2)} / 151.25 hrs
                </div>
                <button
                  onClick={async () => {
                    setRunning(true);
                    setStatus('Running scheduler...');
                    try {
                      const res = await fetch('http://localhost:3001/run-scheduler', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ org_id: orgId, week_of: WEEK_OF })
                      });
                      const data = await res.json();
                      setStatus(`✅ Done — ${data.assignmentsMade} assignments made`);
                      await loadData();
                    } catch (e) {
                      setStatus('✅ Schedule refreshed');
                      await loadData();
                    }
                    setRunning(false);
                  }}
                  style={{
                    background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.green})`,
                    color: '#fff', border: 'none', borderRadius: 8,
                    padding: '10px 24px', fontSize: 14, fontWeight: 700,
                    cursor: 'pointer', opacity: running ? 0.7 : 1
                  }}
                >
                  {running ? 'Running...' : '⚡ Run Scheduler'}
                </button>
              </div>
            </div>

            {status && (
              <div style={{ background: '#d0f0eb', border: `1px solid ${COLORS.green}`, borderRadius: 8, padding: '10px 16px', marginBottom: 20, color: COLORS.green, fontWeight: 600, fontSize: 14 }}>
                {status}
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: 60, color: COLORS.textBody }}>Loading schedule...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {shiftsByDay.map(({ day, shifts: dayShifts }) => (
                  <div key={day} style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ background: COLORS.navy, padding: '10px 20px' }}>
                      <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{day}</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: COLORS.bg }}>
                          {['Shift','Hours','Person 1','Person 2'].map(h => (
                            <th key={h} style={{ padding: '8px 20px', textAlign: 'left', fontSize: 11, color: COLORS.textMuted, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dayShifts.map(shift => {
                          const assignment = getAssignment(shift.id);
                          const p1 = assignment?.person_1;
                          const p2 = assignment?.person_2;
                          return (
                            <tr key={shift.id} style={{ borderTop: '1px solid #eef1f8' }}>
                             <td style={{ padding: '12px 20px', fontSize: 13, fontWeight: 600, color: COLORS.navy }}>{formatShiftTime(shift)}</td>
                              <td style={{ padding: '12px 20px', fontSize: 13, color: COLORS.textBody }}>{shift.hours}h</td>
                              <td style={{ padding: '12px 20px' }}>
                                {p1 ? pill('#dbeafe','#1555a0', p1) : pill('#fee2e2','#b91c1c','Unfilled')}
                              </td>
                              <td style={{ padding: '12px 20px' }}>
                                {p2 ? pill('#dbeafe','#1555a0', p2) : shift.people_needed === 1
                                  ? <span style={{ color: COLORS.textMuted, fontSize: 12 }}>Single coverage</span>
                                  : pill('#fee2e2','#b91c1c','Unfilled')
                                }
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── STAFF TAB ── */}
        {tab === 'staff' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div>
                <h1 style={{ fontSize: 26, fontWeight: 800, color: COLORS.navy, margin: 0, letterSpacing: '-0.03em' }}>Staff</h1>
                <p style={{ color: COLORS.textBody, marginTop: 4, fontSize: 14 }}>{employees.length} employees · click any row to edit</p>
              </div>
            </div>

            {/* ── MANAGE ROLES DRAWER ── */}
            <div style={{ marginBottom: 24 }}>
              <button
                onClick={() => setRolesDrawerOpen(o => !o)}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: '#fff',
                  border: `1px solid ${rolesDrawerOpen ? COLORS.greenBright : COLORS.border}`,
                  borderRadius: rolesDrawerOpen ? '12px 12px 0 0' : 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 20 }}>⚙️</span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.navy }}>Manage Staff Roles</div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 1 }}>Add, rename, or remove the roles used by your organization</div>
                  </div>
                </div>
                <span style={{ fontSize: 18, color: COLORS.textMuted, transform: rolesDrawerOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
              </button>

              {rolesDrawerOpen && (
                <div style={{ background: '#fff', border: `1px solid ${COLORS.greenBright}`, borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
                  {roles.map((role, i) => (
                    <RoleRow
                      key={role.id}
                      role={role}
                      isLast={i === roles.length - 1}
                      onDelete={async () => {
                        const { error } = await supabase.from('roles').delete().eq('id', role.id);
                        if (!error) setRoles(prev => prev.filter(r => r.id !== role.id));
                      }}
                      onRename={async (newName) => {
                        const { error } = await supabase.from('roles').update({ name: newName }).eq('id', role.id);
                        if (!error) setRoles(prev => prev.map(r => r.id === role.id ? { ...r, name: newName } : r));
                      }}
                      COLORS={COLORS}
                      inputStyle={{ padding: '6px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, color: COLORS.navy, fontFamily: 'Inter, sans-serif', outline: 'none' }}
                    />
                  ))}
                  <AddRoleRow
                    onAdd={async (name) => {
                      const { data, error } = await supabase.from('roles').insert({ org_id: orgId, name }).select().single();
                      if (!error && data) setRoles(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
                    }}
                    COLORS={COLORS}
                  />
                </div>
              )}
            </div>

            {saveMsg && (
              <div style={{ background: saveMsg.startsWith('✅') ? '#d0f0eb' : '#fee2e2', border: `1px solid ${saveMsg.startsWith('✅') ? COLORS.green : '#b91c1c'}`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: saveMsg.startsWith('✅') ? COLORS.green : '#b91c1c', fontWeight: 600, fontSize: 14 }}>
                {saveMsg}
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: 60, color: COLORS.textBody }}>Loading staff...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {employees.map(emp => {
                  const isEditing = editingId === emp.id;
                  return (
                    <div
                      key={emp.id}
                      style={{
                        background: '#fff',
                        border: `1px solid ${isEditing ? COLORS.greenBright : COLORS.border}`,
                        borderRadius: 12,
                        overflow: 'hidden',
                        transition: 'border-color 0.2s',
                        boxShadow: isEditing ? `0 0 0 3px rgba(34,197,122,0.1)` : 'none',
                      }}
                    >
                      {/* Row header — always visible */}
                      <div
                        onClick={() => isEditing ? null : startEdit(emp)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '14px 20px',
                          cursor: isEditing ? 'default' : 'pointer',
                          background: isEditing ? '#f8fff9' : '#fff',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.green})`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0
                          }}>
                            {emp.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.navy }}>{emp.name}</div>
                            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 1 }}>{emp.role || 'No role set'}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {emp.priority && pill(
                            emp.priority === 'High' ? '#dbeafe' : emp.priority === 'Low' ? '#f3f4f6' : '#d0f0eb',
                            emp.priority === 'High' ? '#1555a0' : emp.priority === 'Low' ? '#4a5578' : '#0f7a62',
                            emp.priority
                          )}
                          <span style={{ fontSize: 12, color: COLORS.textMuted }}>{emp.max_hours}h/wk</span>
                          {!isEditing && (
                            <span style={{ fontSize: 12, color: COLORS.green, fontWeight: 600 }}>Edit →</span>
                          )}
                        </div>
                      </div>

                      {/* Inline edit form */}
                      {isEditing && (
                        <div style={{ padding: '0 20px 20px', borderTop: `1px solid ${COLORS.border}` }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 16 }}>

                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Name</label>
                              <input style={inputStyle} value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                            </div>

                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Role</label>
                              <select style={inputStyle} value={editForm.role || ''} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}>
                                <option value="">Select role...</option>
                                {roles.map(r => (
                                  <option key={r.id} value={r.name}>{r.name}</option>
                                ))}
                                <option value="__add_new__">＋ Add a new role...</option>
                              </select>
                              {editForm.role === '__add_new__' && (
                                <AddRoleInline
                                  onAdd={async (name) => {
                                    const { data, error } = await supabase.from('roles').insert({ org_id: orgId, name }).select().single();
                                    if (!error && data) {
                                      setRoles(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
                                      setEditForm(f => ({ ...f, role: name }));
                                    }
                                  }}
                                  onCancel={() => setEditForm(f => ({ ...f, role: '' }))}
                                  COLORS={COLORS}
                                />
                              )}
                            </div>

                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Priority</label>
                              <select style={inputStyle} value={editForm.priority || ''} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}>
                                <option value="">Select...</option>
                                <option>High</option>
                                <option>Medium</option>
                                <option>Low</option>
                              </select>
                            </div>

                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Max Hours / Week</label>
                              <input style={inputStyle} type="number" value={editForm.max_hours || ''} onChange={e => setEditForm(f => ({ ...f, max_hours: e.target.value }))} />
                            </div>

                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Max Hours / Month</label>
                              <input style={inputStyle} type="number" value={editForm.max_hours_monthly || ''} onChange={e => setEditForm(f => ({ ...f, max_hours_monthly: e.target.value }))} />
                            </div>

                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Max Hours / Quarter</label>
                              <input style={inputStyle} type="number" value={editForm.max_hours_quarterly || ''} onChange={e => setEditForm(f => ({ ...f, max_hours_quarterly: e.target.value }))} />
                            </div>

                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Email</label>
                              <input style={inputStyle} type="email" value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                            </div>

                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Phone</label>
                              <input style={inputStyle} type="tel" value={editForm.phone || ''} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                            </div>

                            <div>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Certifications</label>
                              <input style={inputStyle} value={editForm.certifications || ''} onChange={e => setEditForm(f => ({ ...f, certifications: e.target.value }))} placeholder="e.g. CPR, Lifeguard" />
                            </div>

                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Available Days</label>
                              <input style={inputStyle} value={editForm.available_days || ''} onChange={e => setEditForm(f => ({ ...f, available_days: e.target.value }))} placeholder="e.g. Mon, Tue, Wed" />
                            </div>

                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Preferred Shifts</label>
                              <input style={inputStyle} value={editForm.preferred_shifts || ''} onChange={e => setEditForm(f => ({ ...f, preferred_shifts: e.target.value }))} placeholder="e.g. Morning, Evening" />
                            </div>

                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Notes</label>
                              <textarea
                                style={{ ...inputStyle, height: 72, resize: 'vertical' }}
                                value={editForm.notes || ''}
                                onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                                placeholder="Any scheduling notes..."
                              />
                            </div>

                          </div>

                          {/* Save / Cancel */}
                          <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
                            <button
                              onClick={cancelEdit}
                              style={{ padding: '9px 20px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: '#fff', fontSize: 13, fontWeight: 600, color: COLORS.textBody, cursor: 'pointer' }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={saveEdit}
                              disabled={saving}
                              style={{
                                padding: '9px 24px', borderRadius: 8, border: 'none',
                                background: saving ? COLORS.textMuted : COLORS.greenBright,
                                fontSize: 13, fontWeight: 700, color: COLORS.navy,
                                cursor: saving ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── SETUP TAB ── */}
        {tab === 'setup' && (
          <OrgSetupWizard onComplete={() => setTab('schedule')} />
        )}

      </div>
    </div>
  );
}

function RoleRow({ role, isLast, onDelete, onRename, COLORS, inputStyle }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(role.name);
  const [saving, setSaving] = useState(false);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: isLast ? 'none' : `1px solid ${COLORS.border}` }}>
      {editing ? (
        <input
          style={{ ...inputStyle, flex: 1, marginRight: 12 }}
          value={name}
          onChange={e => setName(e.target.value)}
          autoFocus
        />
      ) : (
        <span style={{ fontSize: 14, fontWeight: 500, color: COLORS.navy }}>{role.name}</span>
      )}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {editing ? (
          <>
            <button
              onClick={async () => {
                setSaving(true);
                await onRename(name);
                setSaving(false);
                setEditing(false);
              }}
              style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: COLORS.greenBright, color: COLORS.navy, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >
              {saving ? '...' : 'Save'}
            </button>
            <button
              onClick={() => { setName(role.name); setEditing(false); }}
              style={{ padding: '5px 14px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: '#fff', color: COLORS.textBody, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setEditing(true)}
              style={{ padding: '5px 14px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: '#fff', color: COLORS.textBody, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              Rename
            </button>
            <button
              onClick={onDelete}
              style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: '#fee2e2', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >
              Delete
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function AddRoleRow({ onAdd, COLORS }) {
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);

  return (
    <div style={{ padding: '14px 20px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: 10, alignItems: 'center' }}>
      <input
        style={{ flex: 1, padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, color: COLORS.navy, fontFamily: 'Inter, sans-serif', outline: 'none' }}
        placeholder="New role name..."
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={async e => {
          if (e.key === 'Enter' && name.trim()) {
            setAdding(true);
            await onAdd(name.trim());
            setName('');
            setAdding(false);
          }
        }}
      />
      <button
        onClick={async () => {
          if (!name.trim()) return;
          setAdding(true);
          await onAdd(name.trim());
          setName('');
          setAdding(false);
        }}
        style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: COLORS.greenBright, color: COLORS.navy, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: adding ? 0.7 : 1 }}
      >
        {adding ? '...' : '+ Add Role'}
      </button>
    </div>
  );
}

function AddRoleInline({ onAdd, onCancel, COLORS }) {
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);

  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
      <input
        autoFocus
        style={{ flex: 1, padding: '5px 8px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12, fontFamily: 'Inter, sans-serif', outline: 'none' }}
        placeholder="Type new role name..."
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={async e => {
          if (e.key === 'Enter' && name.trim()) {
            setAdding(true);
            await onAdd(name.trim());
            setAdding(false);
          }
          if (e.key === 'Escape') onCancel();
        }}
      />
      <button
        onClick={async () => {
          if (!name.trim()) return;
          setAdding(true);
          await onAdd(name.trim());
          setAdding(false);
        }}
        style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: COLORS.greenBright, color: COLORS.navy, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
      >
        {adding ? '...' : 'Save'}
      </button>
      <button
        onClick={onCancel}
        style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: '#fff', color: COLORS.textBody, fontSize: 12, cursor: 'pointer' }}
      >
        Cancel
      </button>
    </div>
  );
}
